import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, AlertCircle, ExternalLink, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FB_TOKEN_URL = 'https://developers.facebook.com/tools/explorer/';
const FB_SYSUSER_URL = 'https://business.facebook.com/settings/system-users';

const FbTokenConfig = () => {
  const [cfg, setCfg] = useState(null);
  const [newToken, setNewToken] = useState('');
  const [newAccount, setNewAccount] = useState('');
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/fb-config`);
      setCfg(data);
      setNewAccount(data.ad_account_id || '');
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!newToken && !newAccount) {
      toast.error('יש להזין טוקן חדש או ID חשבון');
      return;
    }
    setSaving(true);
    try {
      const body = {};
      if (newToken) body.access_token = newToken;
      if (newAccount && newAccount !== cfg?.ad_account_id) body.ad_account_id = newAccount;
      await axios.post(`${API}/admin/fb-config`, body);
      toast.success('עודכן בהצלחה - בודק...');
      setNewToken('');
      // Wait a bit then reload status
      setTimeout(load, 600);
      // Also refresh the page so fbStats and fbCampaigns re-fetch
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error('שגיאה: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  if (!cfg) return null;

  const isValid = cfg.token_status === 'valid';
  const badgeColor = isValid ? 'bg-green-500' : 'bg-red-500';
  const badgeText = isValid ? 'טוקן תקין' : (cfg.token_status === 'invalid' ? 'טוקן לא תקין' : 'שגיאה');

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200 mb-3" data-testid="fb-token-config">
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-stone-50 transition-colors"
        onClick={() => setExpanded((v) => !v)}
        data-testid="fb-token-toggle"
      >
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
          <span className="font-bold text-sm text-[#2C3E2D]" style={{ fontFamily: 'Heebo, sans-serif' }}>טוקן פייסבוק</span>
          <span className={`${badgeColor} text-white text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1`}>
            {isValid ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
            {badgeText}
          </span>
          {!isValid && cfg.token_error && (
            <span className="text-[11px] text-red-600 truncate max-w-md">{cfg.token_error}</span>
          )}
        </div>
        <span className="text-stone-400 text-xs">{expanded ? '▲ סגור' : '▼ פתח'}</span>
      </div>

      {expanded && (
        <div className="p-4 pt-0 border-t border-stone-200 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <a
              href={FB_TOKEN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-[#1877F2] hover:bg-[#0d5bd4] text-white rounded-lg py-2.5 text-sm font-bold transition-colors"
              data-testid="fb-open-token-explorer"
            >
              <ExternalLink className="w-4 h-4" />
              צור טוקן חדש (Graph Explorer)
            </a>
            <a
              href={FB_SYSUSER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-stone-700 hover:bg-stone-800 text-white rounded-lg py-2.5 text-sm font-bold transition-colors"
              data-testid="fb-open-sysuser"
            >
              <ExternalLink className="w-4 h-4" />
              טוקן קבוע (System User)
            </a>
          </div>

          <div>
            <label className="block text-xs font-bold text-stone-600 mb-1">טוקן חדש</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newToken}
                onChange={(e) => setNewToken(e.target.value)}
                placeholder="EAAxxx... (הדבק את הטוקן החדש כאן)"
                className="flex-1 px-3 py-2 border border-stone-300 rounded-lg text-sm font-mono"
                data-testid="fb-token-input"
                dir="ltr"
              />
              <button
                onClick={save}
                disabled={saving || !newToken}
                className="bg-[#D97706] hover:bg-[#b86205] disabled:bg-stone-300 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-1 transition-colors"
                data-testid="fb-token-save"
              >
                <Send className="w-4 h-4" />
                שגר
              </button>
            </div>
            <p className="text-[11px] text-stone-400 mt-1">
              טוקן נוכחי: <span className="font-mono">{cfg.token_preview}</span> ({cfg.token_length} תווים)
            </p>
          </div>

          <div>
            <label className="block text-xs font-bold text-stone-600 mb-1">Ad Account ID</label>
            <input
              type="text"
              value={newAccount}
              onChange={(e) => setNewAccount(e.target.value)}
              placeholder="610869624681155"
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm font-mono"
              data-testid="fb-account-input"
              dir="ltr"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FbTokenConfig;
